import { useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, Listbox, Transition } from '@headlessui/react';
import AppURL from '../components/AppUrl';

interface UserData {
  name: string;
  email: string;
  id: string;
  role: string;
}

interface TimeEntry {
  id: string;
  _id: string;
  date: string;
  startTime: string;
  endTime: string;
  breakDuration: number;
  employeeId: string;
  employeeName?: string;

  // ✅ NEW (back)
  endsNextDay?: boolean;
}

interface Employee extends UserData {}

const TimeCard = () => {
  // ✅ Utils: date locale dans le fuseau du navigateur
  const getUserTimeZone = () =>
    Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

  const formatLocalDate = (d: Date, tz: string) =>
    d.toLocaleDateString('en-CA', { timeZone: tz }); // YYYY-MM-DD

  const [userTimeZone] = useState(getUserTimeZone());
  const [currentDate, setCurrentDate] = useState<string>(() =>
    formatLocalDate(new Date(), getUserTimeZone())
  );

  // États communs
  const [currentUser, setCurrentUser] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Manager
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(
    null
  );
  const [dateRange, setDateRange] = useState({
    start: currentDate,
    end: currentDate,
  });
  const [showDateModal, setShowDateModal] = useState(false);
  const [reportData, setReportData] = useState<any>(null);

  // Employé : gestion d’une time card par date
  const [selectedDate, setSelectedDate] = useState(currentDate);
  const [timeCardToUpdate, setTimeCardToUpdate] = useState<TimeEntry | null>(
    null
  );
  const [updateStartTime, setUpdateStartTime] = useState('');
  const [updateEndTime, setUpdateEndTime] = useState('');
  const [updateBreakDuration, setUpdateBreakDuration] = useState(0);

  // ✅ NEW: overnight toggle
  const [endsNextDay, setEndsNextDay] = useState(false);

  // Employé : rapport sur une période
  const [showIntervalModal, setShowIntervalModal] = useState(false);
  const [reportRange, setReportRange] = useState({
    start: currentDate,
    end: currentDate,
  });

  // ✅ Recalcule la date locale à minuit du fuseau user
  useEffect(() => {
    const tick = () => setCurrentDate(formatLocalDate(new Date(), userTimeZone));
    const id = setInterval(tick, 60_000); // vérifie toutes les 60s
    return () => clearInterval(id);
  }, [userTimeZone]);

  // ✅ Helper: suggestion auto overnight
  const shouldSuggestOvernight = (start: string, end: string) => {
    if (!start || !end) return false;
    // HH:mm strings compare OK when zero-padded
    return end < start;
  };

  // ✅ Auto-guess endsNextDay when times change
  useEffect(() => {
    // si l'utilisateur a mis une fin "plus petite", on active le lendemain
    if (updateStartTime && updateEndTime) {
      const suggest = shouldSuggestOvernight(updateStartTime, updateEndTime);
      if (suggest) setEndsNextDay(true);
      // si l'utilisateur remet des heures normales, on désactive automatiquement
      if (!suggest) setEndsNextDay(false);
    }
  }, [updateStartTime, updateEndTime]);

  // 📌 Charger la time card d'un employé pour une date donnée
  const loadEmployeeDate = async (employeeId: string, targetDate: string) => {
    try {
      const response = await axios.get(`${AppURL}/api/time-entries/employe`, {
        params: {
          employeeId,
          date: targetDate,
        },
      });

      setSelectedDate(targetDate);

      if (response.data?.success && response.data?.timeEntry) {
        // Fiche trouvée
        const entry = response.data.timeEntry as TimeEntry;

        setTimeCardToUpdate(entry);
        setUpdateStartTime(entry.startTime);
        setUpdateEndTime(entry.endTime || '');
        setUpdateBreakDuration(entry.breakDuration || 0);

        // ✅ read flag from back OR infer from times
        const inferred =
          !!entry.endsNextDay ||
          (entry.endTime && shouldSuggestOvernight(entry.startTime, entry.endTime)) || false;
        setEndsNextDay(inferred);
      } else {
        // Aucune fiche → on prépare une création
        setTimeCardToUpdate(null);
        setUpdateStartTime('09:00');
        setUpdateEndTime('17:00');
        setUpdateBreakDuration(30);
        setEndsNextDay(false);
      }
    } catch (err) {
      console.error('Erreur loadEmployeeDate:', err);
      setError('Erreur lors de la récupération du time card');
    }
  };

  // Chargement initial
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const userData = localStorage.getItem('userData');
        if (!userData) {
          throw new Error('Utilisateur non connecté');
        }

        const parsedUser = JSON.parse(userData) as UserData;
        setCurrentUser(parsedUser);

        // Manager → liste employés
        if (parsedUser.role === 'manager') {
          const employeesResponse = await axios.get(
            `${AppURL}/api/employees/All`
          );

          let employeesData = Array.isArray(employeesResponse.data)
            ? employeesResponse.data
            : employeesResponse.data.employees || [];

          employeesData = employeesData.map((employee: any) => ({
            ...employee,
            id: employee._id || employee.id,
          }));

          setEmployees(employeesData);
        }

        // Employé → charger la fiche d'aujourd'hui
        if (parsedUser.role === 'employe') {
          await loadEmployeeDate(parsedUser.id, currentDate);
        }
      } catch (err) {
        console.error('Erreur de chargement:', err);
        setError('Erreur lors du chargement des données');
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, [currentDate]);

  // ✅ Validation des champs (corrigée pour overnight)
  const validateTimeFields = () => {
    if (!selectedDate || !currentUser?.id) return false;

    if (!updateStartTime || !updateEndTime) {
      alert('Heures de début et de fin requises.');
      return false;
    }

    if (updateBreakDuration < 0 || isNaN(updateBreakDuration)) {
      alert('La durée de pause doit être un nombre positif.');
      return false;
    }

    const timeRegex = /^([0-1]\d|2[0-3]):([0-5]\d)$/;
    if (!timeRegex.test(updateStartTime) || !timeRegex.test(updateEndTime)) {
      alert("Format d'heure invalide (ex: 08:30)");
      return false;
    }

    // ✅ If NOT overnight, end must be after start
    if (!endsNextDay && updateStartTime >= updateEndTime) {
      alert(
        "L'heure de début doit être avant l'heure de fin (ou cochez “Fin le lendemain”)."
      );
      return false;
    }

    // ✅ If overnight, end is allowed to be smaller
    // but you can still block exact same time (optional)
    if (endsNextDay && updateStartTime === updateEndTime) {
      alert("L'heure de fin ne peut pas être identique à l'heure de début.");
      return false;
    }

    return true;
  };

  // ✅ Créer une time card (payload corrigé)
  const handleCreateTimeCard = async () => {
    if (!validateTimeFields() || !currentUser) return;

    try {
      const payload = {
        employeeId: currentUser.id,
        date: selectedDate,
        startTime: updateStartTime,
        endTime: updateEndTime,
        breakDuration: updateBreakDuration,
        endsNextDay, // ✅ NEW
      };

      const res = await axios.post(`${AppURL}/api/time-entries`, payload);

      if (res.data?.timeEntry) {
        await loadEmployeeDate(currentUser.id, selectedDate);
        alert('Time card créée avec succès.');
      }
    } catch (err: any) {
      console.error('Erreur:', err);
      const msg =
        err?.response?.data?.message ||
        'Erreur lors de la création du time card';
      setError(msg);
      alert(msg);
    }
  };

  // ✅ Mettre à jour une time card (payload corrigé)
  const handleUpdateTimeCard = async () => {
    if (!timeCardToUpdate?._id) return;
    if (!validateTimeFields() || !currentUser) return;

    try {
      await axios.put(
        `${AppURL}/api/time-entries/update/${timeCardToUpdate._id}`,
        {
          startTime: updateStartTime,
          endTime: updateEndTime,
          breakDuration: updateBreakDuration,
          endsNextDay, // ✅ NEW
        }
      );

      await loadEmployeeDate(currentUser.id, selectedDate);
      alert('Time card mise à jour avec succès');
    } catch (err) {
      console.error('Erreur:', err);
      setError('Erreur lors de la mise à jour du time card');
      alert('Erreur lors de la mise à jour du time card');
    }
  };

  // ✅ Supprimer une time card
  const handleDeleteTimeCard = async () => {
    if (!timeCardToUpdate?._id || !currentUser) return;

    const confirmDelete = window.confirm(
      'Confirmer la suppression de cette fiche ?'
    );
    if (!confirmDelete) return;

    try {
      await axios.delete(
        `${AppURL}/api/time-entries/delete/${timeCardToUpdate._id}`
      );

      setTimeCardToUpdate(null);
      setUpdateStartTime('09:00');
      setUpdateEndTime('17:00');
      setUpdateBreakDuration(30);
      setEndsNextDay(false);

      alert('Time card supprimée avec succès.');
    } catch (err) {
      console.error('Erreur:', err);
      setError('Erreur lors de la suppression du time card');
      alert('Erreur lors de la suppression du time card');
    }
  };

  // ✅ Rapport employé sur une période
  const generateSelfReport = async () => {
    if (!currentUser?.id) return;

    try {
      const response = await axios.get(`${AppURL}/api/time-entries/report`, {
        params: {
          employeeId: currentUser.id,
          startDate: reportRange.start,
          endDate: reportRange.end,
        },
      });

      setReportData(response.data?.report || null);
      setShowIntervalModal(false);
    } catch (err) {
      console.error('Erreur:', err);
      setError('Erreur lors de la génération du rapport');
      alert('Erreur lors de la génération du rapport');
    }
  };

  // ✅ Rapport manager sur une période
  const generateEmployeeReport = async () => {
    if (!selectedEmployee) return;

    try {
      const response = await axios.get(`${AppURL}/api/time-entries/report`, {
        params: {
          employeeId: selectedEmployee.id,
          startDate: dateRange.start,
          endDate: dateRange.end,
        },
      });
      setReportData(response.data);

      setShowDateModal(false);
    } catch (err) {
      console.error('Erreur:', err);
      setError('Erreur lors de la génération du rapport');
    }
  };

  // === RENDU COMMUN ===
  if (loading) return <div className="p-4">Chargement...</div>;
  if (error) return <div className="p-4 text-red-500">{error}</div>;
  if (!currentUser) return <div className="p-4">Utilisateur non connecté</div>;

  // =======================
  // VUE EMPLOYÉ
  // =======================
  if (currentUser.role === 'employe') {
    const hasEntry = !!timeCardToUpdate;

    return (
      <div className="max-w-xl mx-auto p-4 space-y-6 mt-20">
        {/* Header */}
        <div className="bg-gradient-to-r from-gray-800 to-gray-700 text-white rounded-xl p-6 shadow-lg flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold">Bonjour {currentUser.name}</h2>
            <p className="text-blue-100 text-sm">
              Gérez vos fiches de temps pour chaque journée
            </p>
          </div>
          <div className="text-right text-xs text-blue-100">
            <div>Aujourd&apos;hui</div>
            <div className="font-semibold">{currentDate}</div>
          </div>
        </div>

        {/* Carte principale : date + fiche */}
        <div className="bg-white rounded-xl shadow-md p-5 space-y-4 border border-gray-100">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div>
              <h3 className="font-semibold text-lg">Ma fiche de temps</h3>
              <p className="text-xs text-gray-500">
                Choisissez une date, puis remplissez vos heures.
              </p>
            </div>
          </div>

          {/* Message selon existence de la fiche */}
          <div
            className={`text-xs px-3 py-2 rounded-lg ${
              hasEntry
                ? 'bg-emerald-50 text-emerald-700 border border-emerald-100'
                : 'bg-yellow-50 text-yellow-800 border border-yellow-100'
            }`}
          >
            {hasEntry ? (
              <>
                Une fiche existe déjà pour cette date. Vous pouvez la{' '}
                <span className="font-semibold">modifier</span> ou la{' '}
                <span className="font-semibold">supprimer</span>.
              </>
            ) : (
              <>
                Aucune fiche trouvée pour cette date. Vous pouvez en{' '}
                <span className="font-semibold">créer une</span>.
              </>
            )}
          </div>

          {/* Date */}
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">
              Date de la fiche
            </label>
            <input
              type="date"
              value={selectedDate}
              max={currentDate}
              onChange={async (e) => {
                const newDate = e.target.value;
                setSelectedDate(newDate);
                if (currentUser?.id && newDate) {
                  await loadEmployeeDate(currentUser.id, newDate);
                }
              }}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
            />
          </div>

          {/* Heures */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-700">
                Heure de début
              </label>
              <input
                type="time"
                value={updateStartTime}
                onChange={(e) => setUpdateStartTime(e.target.value)}
                className="mt-1 w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
              />
            </div>

            <div>
              <label className="text-xs font-medium text-gray-700">
                Heure de fin
              </label>
              <input
                type="time"
                value={updateEndTime}
                onChange={(e) => setUpdateEndTime(e.target.value)}
                className="mt-1 w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
              />

              {/* ✅ Overnight toggle */}
              <label className="mt-2 flex items-center gap-2 text-xs text-gray-600 select-none">
                <input
                  type="checkbox"
                  checked={endsNextDay}
                  onChange={(e) => setEndsNextDay(e.target.checked)}
                />
                Fin le lendemain
              </label>

              {/* ✅ small hint */}
              {updateStartTime && updateEndTime && shouldSuggestOvernight(updateStartTime, updateEndTime) && !endsNextDay && (
                <div className="mt-1 text-[11px] text-amber-700">
                  Astuce: votre heure de fin est plus petite que l&apos;heure de début — cochez “Fin le lendemain”.
                </div>
              )}
            </div>

            <div>
              <label className="text-xs font-medium text-gray-700">
                Pause (minutes)
              </label>
              <input
                type="number"
                min={0}
                value={updateBreakDuration}
                onChange={(e) =>
                  setUpdateBreakDuration(parseInt(e.target.value) || 0)
                }
                className="mt-1 w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row justify-between gap-2 mt-2">
            <div className="flex gap-2">
              <button
                onClick={() =>
                  hasEntry ? handleUpdateTimeCard() : handleCreateTimeCard()
                }
                className="flex-1 sm:flex-none inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium rounded-lg text-white bg-gray-900 hover:bg-gray-800 transition"
              >
                {hasEntry
                  ? 'Enregistrer les modifications'
                  : 'Créer cette fiche'}
              </button>

              {hasEntry && (
                <button
                  onClick={handleDeleteTimeCard}
                  className="inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium rounded-lg border border-red-200 text-red-600 hover:bg-red-50 transition"
                >
                  Supprimer
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Rapport sur une période */}
        <div className="bg-white rounded-xl shadow-md p-5 space-y-4 border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-lg">Mes heures sur une période</h3>
              <p className="text-xs text-gray-500">
                Sélectionnez une plage de dates pour voir un résumé.
              </p>
            </div>
            <button
              onClick={() => setShowIntervalModal(true)}
              className="text-xs px-3 py-1.5 rounded-full border border-gray-300 hover:bg-gray-100"
            >
              Choisir une période
            </button>
          </div>

          {reportData && (
            <div className="bg-gray-50 rounded-lg p-4 text-sm space-y-2">
              <h4 className="font-semibold">
                Rapport du {reportRange.start} au {reportRange.end}
              </h4>
              <ul className="space-y-1">
                {reportData.dailyData?.map((d: any) => (
                  <li key={d.date}>
                    {d.date} – {Number(d.hoursWorked).toFixed(2)}h (pause{' '}
                    {d.breakDuration} min)
                    {d.endsNextDay ? (
                      <span className="ml-2 text-xs text-gray-500">
                        (lendemain)
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
              <div className="mt-2 font-bold text-gray-800">
                Total travaillé : {Number(reportData.totalHours || 0).toFixed(2)} heures
              </div>
            </div>
          )}

          {/* Modal période employé */}
          <Transition show={showIntervalModal}>
            <Dialog
              onClose={() => setShowIntervalModal(false)}
              className="fixed inset-0 z-10"
            >
              <div className="flex items-center justify-center min-h-screen p-4">
                <div className="fixed inset-0 bg-black opacity-30" />
                <div className="relative bg-white rounded-lg max-w-sm w-full p-6">
                  <Dialog.Title className="text-lg font-semibold mb-4">
                    Sélectionner une période
                  </Dialog.Title>
                  <div className="space-y-3 text-sm">
                    <div>
                      <label className="block mb-1">Date début</label>
                      <input
                        type="date"
                        value={reportRange.start}
                        max={currentDate}
                        onChange={(e) =>
                          setReportRange((prev) => ({
                            ...prev,
                            start: e.target.value,
                          }))
                        }
                        className="w-full border p-2 rounded"
                      />
                    </div>
                    <div>
                      <label className="block mb-1">Date fin</label>
                      <input
                        type="date"
                        value={reportRange.end}
                        max={currentDate}
                        onChange={(e) =>
                          setReportRange((prev) => ({
                            ...prev,
                            end: e.target.value,
                          }))
                        }
                        className="w-full border p-2 rounded"
                      />
                    </div>
                  </div>
                  <div className="mt-4 flex justify-end space-x-2">
                    <button
                      onClick={() => setShowIntervalModal(false)}
                      className="px-4 py-2 border rounded text-sm"
                    >
                      Annuler
                    </button>
                    <button
                      onClick={generateSelfReport}
                      className="px-4 py-2 bg-gray-900 text-white rounded text-sm"
                    >
                      Voir
                    </button>
                  </div>
                </div>
              </div>
            </Dialog>
          </Transition>
        </div>
      </div>
    );
  }

  // =======================
  // VUE MANAGER
  // =======================
  return (
    <div className="bg-white rounded-lg shadow-md p-4 md:p-6 mx-2 md:mx-auto mt-20 md:mt-20 ">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <div>
          <h2 className="text-lg md:text-xl font-bold">
            Gestion du temps des employés
          </h2>
          <p className="text-sm md:text-base text-gray-600">
            Manager: {currentUser.name}
          </p>
        </div>

        <div className="w-full md:w-auto">
          <label className="block text-sm font-medium mb-1">
            Sélectionner un employé:
          </label>
          <Listbox value={selectedEmployee} onChange={setSelectedEmployee}>
            <div className="relative">
              <Listbox.Button className="w-full bg-white border rounded-md py-2 px-3 text-sm md:text-base text-left">
                {selectedEmployee?.name || 'Sélectionner...'}
              </Listbox.Button>
              <Listbox.Options className="absolute z-10 w-full bg-white border rounded-md shadow-lg mt-1 max-h-60 overflow-auto text-sm md:text-base">
                {employees.length > 0 ? (
                  employees.map((employee) => (
                    <Listbox.Option
                      key={employee.id}
                      value={employee}
                      className={({ active }) =>
                        `px-3 py-1 ${active ? 'bg-blue-100' : ''}`
                      }
                    >
                      {employee.name} ({employee.role})
                    </Listbox.Option>
                  ))
                ) : (
                  <div className="px-3 py-1 text-gray-500 text-sm">
                    Aucun employé disponible
                  </div>
                )}
              </Listbox.Options>
            </div>
          </Listbox>
        </div>
      </div>

      {selectedEmployee && (
        <div className="space-y-4">
          <button
            onClick={() => setShowDateModal(true)}
            className="w-full md:w-auto bg-gray-950 hover:bg-gray-600 text-white py-2 px-4 rounded text-sm md:text-base"
          >
            Sélectionner une période
          </button>

          {reportData?.report && (
            <div className="mt-4 border-t pt-4">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-3">
                <h3 className="font-bold text-base md:text-lg">
                  Rapport pour {selectedEmployee.name}
                </h3>
                <p className="text-sm text-gray-600">
                  {reportData.report.dateRange.start} à{' '}
                  {reportData.report.dateRange.end}
                </p>
              </div>

              {/* Version mobile - Cards */}
              <div className="md:hidden space-y-3">
                {reportData.report.dailyData.map((day: any) => (
                  <div
                    key={day.date}
                    className="border rounded-lg p-3 shadow-sm"
                  >
                    <div className="font-medium">{day.date}</div>
                    <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
                      <div>
                        Début:{' '}
                        <span className="font-medium">{day.startTime}</span>
                      </div>
                      <div>
                        Fin:{' '}
                        <span className="font-medium">
                          {day.endTime}{' '}
                          {day.endsNextDay ? (
                            <span className="ml-1 text-xs text-gray-500">
                              (lendemain)
                            </span>
                          ) : null}
                        </span>
                      </div>
                      <div>
                        Heures travaillées:{' '}
                        <span className="font-medium">{day.hoursWorked}h</span>
                      </div>
                      <div>
                        Pause:{' '}
                        <span className="font-medium">
                          {day.breakDuration}min
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Version desktop - Table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                        Date
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                        Début
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                        Fin
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                        Heures travaillées
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                        Pause
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {reportData.report.dailyData.map((day: any) => (
                      <tr key={day.date}>
                        <td className="px-4 py-2 whitespace-nowrap text-sm">
                          {day.date}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm">
                          {day.startTime}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm">
                          {day.endTime}{' '}
                          {day.endsNextDay ? (
                            <span className="text-xs text-gray-500">
                              (lendemain)
                            </span>
                          ) : null}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm">
                          {day.hoursWorked}h
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm">
                          {day.breakDuration}min
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4 p-3 bg-gray-50 rounded-lg">
                <div className="flex flex-col items-center p-2 bg-white rounded shadow-sm">
                  <span className="text-sm font-medium text-gray-600">
                    Total heures
                  </span>
                  <span className="text-xl font-bold">
                    {reportData.report.totalHours} h
                  </span>
                </div>
                <div className="flex flex-col items-center p-2 bg-white rounded shadow-sm">
                  <span className="text-sm font-medium text-gray-600">
                    Total pauses
                  </span>
                  <span className="text-xl font-bold">
                    {reportData.report.totalBreak} min
                  </span>
                  <span className="text-xs text-gray-500">
                    ({(reportData.report.totalBreak / 60).toFixed(2)} h)
                  </span>
                </div>
                <div className="flex flex-col items-center p-2 bg-white rounded shadow-sm">
                  <span className="text-sm font-medium text-gray-600">
                    Heures payées
                  </span>
                  <span className="text-xl font-bold">
                    {(
                      reportData.report.totalHours -
                      reportData.report.totalBreak / 60
                    ).toFixed(2)}{' '}
                    h
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modal (inchangé mais responsive) */}
      <Transition show={showDateModal}>
        <Dialog
          onClose={() => setShowDateModal(false)}
          className="fixed z-10 inset-0 overflow-y-auto"
        >
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-black opacity-30" />
            <div className="relative bg-white rounded-lg w-full max-w-sm mx-auto p-4 md:p-6">
              <Dialog.Title className="text-lg font-bold mb-4">
                Sélectionner une période
              </Dialog.Title>
              <div className="space-y-3">
                <div>
                  <label className="block mb-1 text-sm">Date de début:</label>
                  <input
                    type="date"
                    value={dateRange.start}
                    onChange={(e) =>
                      setDateRange({ ...dateRange, start: e.target.value })
                    }
                    className="w-full p-2 border rounded text-sm"
                  />
                </div>
                <div>
                  <label className="block mb-1 text-sm">Date de fin:</label>
                  <input
                    type="date"
                    value={dateRange.end}
                    onChange={(e) =>
                      setDateRange({ ...dateRange, end: e.target.value })
                    }
                    className="w-full p-2 border rounded text-sm"
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-2 mt-4">
                <button
                  onClick={() => setShowDateModal(false)}
                  className="px-3 py-1 md:px-4 md:py-2 border rounded text-sm"
                >
                  Annuler
                </button>
                <button
                  onClick={generateEmployeeReport}
                  className="px-3 py-1 md:px-4 md:py-2 bg-blue-500 text-white rounded text-sm"
                >
                  Générer
                </button>
              </div>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};

export default TimeCard;
